import React, { useState, useEffect } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import DOMPurify from 'dompurify';
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Alignment,
  Link,
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageUpload,
  SimpleUploadAdapter,
  ImageInsert,
  MediaEmbed,
  MediaEmbedToolbar,
  Font,
  HorizontalLine,
  RemoveFormat,
  SpecialCharacters,
  SpecialCharactersEssentials,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  List,
  BlockQuote,
  Indent,
  IndentBlock,
  Bookmark,
  Table,
  TableToolbar,
  TableCaption,
  TableCellProperties,
  TableProperties,
  PasteFromOffice,
  FindAndReplace,
  SelectAll,
  Style,
  Emoji,
  Highlight,
  HtmlEmbed,
  GeneralHtmlSupport,
  Mention,
  Fullscreen,
  SourceEditing,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import coreTranslations from 'ckeditor5/translations/fa.js';
import { toast } from 'react-toastify';

const EditorText = ({ value, onChange, onBlur }) => {
  const [editorTab, setEditorTab] = useState('visual');
  const [editorData, setEditorData] = useState(value || '');
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    setEditorData(value || '');
  }, [value]);

  useEffect(() => {
    const handleStorageChange = () => {
      setToken(localStorage.getItem('token'));
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleEditorChange = (data) => {
    console.log('HTML خروجی CKEditor:', data);
    setEditorData(data);
    onChange(data);
  };

  const handleTabClick = (tab, e) => {
    e.preventDefault();
    setEditorTab(tab);
  };

  // پاک‌سازی HTML با DOMPurify
  const sanitizeHtml = (html) => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p', 'div', 'span', 'br', 'hr',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'strong', 'em', 'u', 's', 'sub', 'sup',
        'ul', 'ol', 'li',
        'a', 'img', 'iframe',
        'table', 'thead', 'tbody', 'tr', 'th', 'td', 'caption',
        'blockquote', 'q',
        'pre', 'code',
        'mark', 'ins', 'del',
      ],
      ALLOWED_ATTR: [
        'class', 'id', 'style', 'title', 'dir', 'lang',
        'href', 'target', 'rel',
        'src', 'alt', 'width', 'height',
        'colspan', 'rowspan', 'align', 'valign',
        'frameborder', 'allowfullscreen',
      ],
      ALLOWED_CLASSES: [
        'big-title', 'quote-text', 'medium-title', 'highlight-text',
        'small-text', 'info-box', 'info-box-2',
        'ck', 'ck-content', 'marker-yellow', 'marker-green', 'marker-pink',
        'marker-blue', 'marker-red',
      ],
      ALLOWED_STYLES: [
        'font-size', 'font-weight', 'color', 'background-color',
        'border', 'border-left', 'padding', 'margin', 'border-radius',
        'box-shadow', 'line-height', 'font-style', 'position', 'left', 'top',
      ],
      FORBID_TAGS: ['script', 'style', 'object', 'embed', 'applet', 'form'],
      FORBID_ATTR: ['on*', 'data-*', 'contenteditable'],
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['allowfullscreen', 'frameborder'],
    });
  };

  return (
    <div className="editor-container">
      <div className="tabs">
        <button
          type="button"
          className={editorTab === 'visual' ? 'tab active' : 'tab'}
          onClick={(e) => handleTabClick('visual', e)}
        >
          ویژوال
        </button>
        <button
          type="button"
          className={editorTab === 'code' ? 'tab active' : 'tab'}
          onClick={(e) => handleTabClick('code', e)}
        >
          کد
        </button>
        <button
          type="button"
          className={editorTab === 'preview' ? 'tab active' : 'tab'}
          onClick={(e) => handleTabClick('preview', e)}
        >
          پیش‌نمایش
        </button>
      </div>
      {editorTab === 'visual' && (
        <CKEditor
          editor={ClassicEditor}
          config={{
            licenseKey: 'GPL',
            plugins: [
              Essentials,
              Paragraph,
              Heading,
              Bold,
              Italic,
              Underline,
              Strikethrough,
              Alignment,
              Link,
              Image,
              ImageToolbar,
              ImageCaption,
              ImageStyle,
              ImageResize,
              ImageUpload,
              SimpleUploadAdapter,
              ImageInsert,
              MediaEmbed,
              MediaEmbedToolbar,
              Font,
              HorizontalLine,
              RemoveFormat,
              SpecialCharacters,
              SpecialCharactersEssentials,
              SpecialCharactersArrows,
              SpecialCharactersCurrency,
              List,
              BlockQuote,
              Indent,
              IndentBlock,
              Bookmark,
              Table,
              TableToolbar,
              TableCaption,
              TableCellProperties,
              TableProperties,
              PasteFromOffice,
              FindAndReplace,
              SelectAll,
              Style,
              Emoji,
              Highlight,
              HtmlEmbed,
              GeneralHtmlSupport,
              Mention,
              Fullscreen,
              SourceEditing,
            ],
            toolbar: {
              items: [
                'undo', 'redo', '|',
                'heading', '|',
                'bold', 'italic', 'underline', 'strikethrough', 'highlight', '|',
                'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor', '|',
                'alignment', 'indent', 'outdent', '|',
                'link', 'imageUpload', 'imageInsert', 'mediaEmbed', 'insertTable', 'htmlEmbed', '|',
                'bookmark', 'specialCharacters', 'emoji', '|',
                'bulletedList', 'numberedList', 'blockQuote', '|',
                'findAndReplace', 'selectAll', 'removeFormat', 'horizontalLine', '|',
                'style', '|',
                'sourceEditing', '|',
                'fullscreen',
              ],
              shouldNotGroupWhenFull: true,
            },
            link: {
              defaultProtocol: 'https://',
              decorators: [
                {
                  mode: 'manual',
                  label: 'باز شدن در تب جدید',
                  attributes: {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  },
                },
              ],
              addTargetToExternalLinks: true,
            },
            image: {
              toolbar: [
                'imageTextAlternative', 'toggleImageCaption', '|',
                'imageStyle:inline', 'imageStyle:block', 'imageStyle:side',
              ],
              upload: {
                types: ['png', 'jpeg', 'webp'],
              },
            },
            table: {
              contentToolbar: [
                'tableColumn', 'tableRow', 'mergeTableCells',
                'tableProperties', 'tableCellProperties', 'toggleTableCaption',
              ],
            },
            mediaEmbed: {
              previewsInData: true,
              providers: [
                {
                  name: 'youtube',
                  url: /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/,
                  html: ({ id }) => `<iframe src="https://www.youtube.com/embed/${id}" frameborder="0" allowfullscreen></iframe>`,
                },
                {
                  name: 'vimeo',
                  url: /^.*(vimeo.com\/|video\/)([^#\&\?]*).*/,
                  html: ({ id }) => `<iframe src="https://player.vimeo.com/video/${id}" frameborder="0" allowfullscreen></iframe>`,
                },
              ],
            },
            fontSize: {
              options: [10, 12, 14, 'default', 18, 24, 36],
            },
            fontFamily: {
              options: ['default', 'Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Vazirmatn'],
            },
            style: {
              definitions: [
                { name: 'عنوان بزرگ', element: 'h2', classes: ['big-title'] },
                { name: 'متن نقل‌قول', element: 'p', classes: ['quote-text'] },
                { name: 'عنوان متوسط', element: 'h3', classes: ['medium-title'] },
                { name: 'متن برجسته', element: 'p', classes: ['highlight-text'] },
                { name: 'متن کوچک', element: 'p', classes: ['small-text'] },
                { name: 'جعبه اطلاعات', element: 'p', classes: ['info-box'] },
                { name: 'جعبه اطلاعات 2', element: 'p', classes: ['info-box-2'] },
              ],
            },
            highlight: {
              options: [
                { model: 'yellowMarker', class: 'marker-yellow', title: 'زرد', color: '#FFFF00', type: 'marker' },
                { model: 'greenMarker', class: 'marker-green', title: 'سبز', color: '#00FF00', type: 'marker' },
                { model: 'pinkMarker', class: 'marker-pink', title: 'صورتی', color: '#FF69B4', type: 'marker' },
                { model: 'blueMarker', class: 'marker-blue', title: 'آبی', color: '#0000FF', type: 'marker' },
                { model: 'redMarker', class: 'marker-red', title: 'قرمز', color: '#FF0000', type: 'marker' },
              ],
            },
            mention: {
              feeds: [
                {
                  marker: '@',
                  feed: ['@کاربر1', '@کاربر2', '@کاربر3'],
                  minimumCharacters: 1,
                },
              ],
            },
            simpleUpload: {
              uploadUrl: `${process.env.REACT_APP_API_BASE_URL}/api/admin/uploads/image`,
              headers: {
                Authorization: token ? `Bearer ${token}` : '',
              },
            },
            language: 'fa',
            translations: [coreTranslations],
            direction: 'rtl',
            htmlSupport: {
              disallow: [
                { name: 'script' },
                { name: 'style' },
                { attributes: [{ key: /^on.*$/i, value: true }] },
              ],
            },
          }}
          data={editorData}
          onChange={(event, editor) => {
            const data = editor.getData();
            handleEditorChange(data);
          }}
          onBlur={onBlur}
          onReady={(editor) => {
            console.log('ویرایشگر آماده است!', editor);
            console.log('پلاگین‌های فعال:', Array.from(editor.plugins.getNames()));
            const linkPlugin = editor.plugins.get('Link');
            console.log('وضعیت پلاگین Link:', linkPlugin ? 'فعال' : 'غیرفعال');
            const linkCommand = editor.commands.get('link');
            console.log('وضعیت دستور Link:', linkCommand ? 'فعال' : 'غیرفعال');
          }}
          onError={(error, { willEditorRestart }) => {
            console.error('خطای CKEditor:', error);
            console.log('جزئیات خطا:', {
              message: error.message,
              status: error.status,
              response: error.response,
            });
            if (error.message.includes('400')) {
              toast.error('خطایی در آپلود تصویر رخ داد. لطفاً نوع یا حجم فایل را بررسی کنید.');
            } else if (error.message.includes('401')) {
              toast.error('لطفاً دوباره وارد شوید.');
            } else {
              toast.error('خطایی در آپلود تصویر رخ داد: ' + error.message);
            }
            if (willEditorRestart) {
              console.warn('CKEditor در حال ری‌استارت است');
            }
          }}
        />
      )}
      {editorTab === 'code' && (
        <div className="code-tab">
          <textarea
            value={editorData}
            onChange={(e) => handleEditorChange(e.target.value)}
            style={{ width: '100%', minHeight: '400px', fontFamily: 'monospace', direction: 'ltr' }}
          />
        </div>
      )}
      {editorTab === 'preview' && (
        <div
          className="preview-tab ck ck-content"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(editorData) }}
        />
      )}
    </div>
  );
};

export default EditorText;